import { FC, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
} from "@mui/material";
import {
  Form,
  FormDataConsumer,
  TextInput,
  required,
  useNotify,
} from "react-admin";
import { AxiosError, TSellerAddress, createAddress } from "@/api";
import { country } from "@/config/country";
import {
  CityInput,
  CountryInput,
  StateInput,
} from "../../shared/location";

/**
 * AddressModalV2 — creates a new address for the given sellerId using the
 * same endpoint suppliers-web hits (POST /api/sellers/:sellerId/address).
 * Layout mirrors backoffice's supplier-commercial-user form: MUI Grid
 * + react-admin location inputs.
 */
export const AddressModalV2: FC<{
  open: boolean;
  onClose: () => void;
  sellerId: string;
  onCreated?: (address: TSellerAddress) => void;
}> = ({ open, onClose, sellerId, onCreated }) => {
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const [isMain, setIsMain] = useState(false);

  const close = () => {
    if (saving) return;
    setIsMain(false);
    onClose();
  };

  const submit = async (values: any) => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        name: values.name?.trim() || values.addressLine1?.trim() || "",
        addressLine1: values.addressLine1?.trim() ?? "",
        addressLine2: values.addressLine2?.trim() ?? "",
        country: values.country ?? "",
        state: values.state ?? "",
        city: values.city ?? "",
        postalCode: values.postalCode ?? "",
        latitude: "0",
        longitude: "0",
        isMain,
      } as unknown as TSellerAddress;
      const res = await createAddress(sellerId, payload);
      notify("Dirección creada", { type: "success" });
      onCreated?.(res);
      close();
    } catch (e) {
      const msg =
        e instanceof AxiosError ? e.response?.data?.message : String(e);
      notify(msg ?? "Error al crear la dirección", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="md"
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>Nueva dirección</DialogTitle>
      <Form
        defaultValues={{
          country: country.code === "co" ? "Colombia" : undefined,
        }}
        onSubmit={submit}
      >
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextInput
                source="name"
                label="Nombre / alias"
                helperText="Ej. Bodega principal"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isMain}
                    onChange={(e) => setIsMain(e.target.checked)}
                  />
                }
                label="Marcar como principal"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <CountryInput
                source="country"
                validate={required()}
                fullWidth
              />
            </Grid>
            <FormDataConsumer>
              {({ formData }) => (
                <>
                  <Grid item xs={12} sm={6}>
                    <StateInput
                      source="state"
                      countrySource="country"
                      validate={required()}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CityInput
                      source="city"
                      stateSource="state"
                      validate={required()}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextInput
                      source="postalCode"
                      label="Código postal"
                      validate={
                        country.code === "co" ? undefined : required()
                      }
                      fullWidth
                    />
                  </Grid>
                </>
              )}
            </FormDataConsumer>

            <Grid item xs={12}>
              <TextInput
                source="addressLine1"
                label="Dirección"
                validate={required()}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source="addressLine2"
                label="Detalles adicionales"
                helperText="Apto, piso, referencia, etc."
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : null}
          >
            Crear dirección
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};
